// Logo de la red de una tarjeta (Visa / Mastercard / Amex) + genérico de fallback.
// Se usa en el portal de facturación (método de pago guardado y form de alta) para
// mostrar la marca real de la tarjeta. Los logos son SVG/markup inline (no dependen
// de internet). La red se detecta por la marca guardada (texto) o por el BIN
// (primeros dígitos) mientras se tipea. Ver ADR-2026-08-03-facturacion-*.

import { CreditCard } from 'lucide-react';

export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'unknown';

/** Detecta la red por el texto de la marca ("Mastercard…") o por el número (BIN). */
export function detectCardBrand(input: string | null | undefined): CardBrand {
  const s = (input || '').toLowerCase();
  if (s.includes('visa')) return 'visa';
  if (s.includes('master')) return 'mastercard';
  if (s.includes('amex') || s.includes('american')) return 'amex';
  const d = (input || '').replace(/\D/g, '');
  if (!d) return 'unknown';
  if (/^4/.test(d)) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(d)) return 'mastercard';
  if (/^3[47]/.test(d)) return 'amex';
  return 'unknown';
}

export function CardBrandIcon({ brand, className = '' }: { brand: CardBrand; className?: string }) {
  const box = `w-9 h-6 rounded-[4px] border border-neutral-200 flex items-center justify-center overflow-hidden shrink-0 ${className}`;

  if (brand === 'visa') {
    return (
      <span className={`${box} bg-white`}>
        <span className="text-[10px] font-extrabold italic tracking-tight" style={{ color: '#1434CB' }}>
          VISA
        </span>
      </span>
    );
  }

  if (brand === 'mastercard') {
    return (
      <span className={`${box} bg-white`}>
        <svg width="24" height="16" viewBox="0 0 32 20" aria-hidden="true">
          <circle cx="13" cy="10" r="8" fill="#EB001B" />
          <circle cx="21" cy="10" r="8" fill="#F79E1B" fillOpacity="0.9" />
        </svg>
      </span>
    );
  }

  if (brand === 'amex') {
    return (
      <span className={box} style={{ background: '#1F72CD' }}>
        <span className="text-white text-[8px] font-bold tracking-tight">AMEX</span>
      </span>
    );
  }

  return (
    <span className={`${box} bg-neutral-100 text-neutral-500`}>
      <CreditCard className="w-4 h-4" />
    </span>
  );
}
